/**
 * applyOps — THE ONE merge function (ARCHITECTURE.md §4.5, ADR-002).
 *
 * PURE, no I/O, deterministic. Imported by exactly three call sites: the client
 * optimistic path, the authoritative server, and the Sheet reconcile. Never
 * mutates its inputs; returns a new state.
 *
 * Semantics (§4.3):
 *  - Per-field LWW: items store `_clocks` per field; HLCs compared per field via
 *    compareHLC (raw string comparison is BANNED — CONSTRAINTS #4).
 *  - Deletes are timestamped tombstone field-writes to the `deleted` field,
 *    competing in field LWW like any other write (kills v1's Sheet resurrection).
 *    No hard deletes, no tombstone GC (CONSTRAINTS #2).
 *  - Create vs update are distinct: an update to an unknown item_id PARKS as a
 *    conflict, never a silent upsert.
 *  - Rejected/stale ops are reported, never dropped (CONSTRAINTS #3) — the caller
 *    routes "parked" to the dead-letter queue and surfaces it.
 */
import type { Op } from "./op.ts";
import { compareHLC, tryParseHLC, type HLC, type ParsedHLC } from "./hlc.ts";

/** The tombstone field name (§4.3 / ADR-002: "delete is a timestamped field-write to `deleted`"). */
export const TOMBSTONE_FIELD = "deleted";

export type FieldValues = Record<string, unknown>;

/** One materialized row: current field values + one HLC clock per field. */
export interface Item {
  fields: FieldValues;
  /** Per-field LWW clocks — §4.3 "items store `_clocks` per field". */
  _clocks: Record<string, HLC>;
}

/**
 * The in-memory state of one gig that applyOps folds ops into. The server
 * materializes this from/to its `items` table; the client from IndexedDB; the
 * Sheet reconcile from the Sheet — all through this same shape.
 */
export interface GigState {
  gig_id: string;
  /** Gig-level fields (create_gig/delete_gig target this). null = gig not created yet. */
  gig: Item | null;
  /** collection key -> item_id -> Item. Keys come from ops; sync-core never enumerates collection names (CONSTRAINTS #5). */
  collections: Record<string, Record<string, Item>>;
  /** op_ids already folded in — idempotent replay returns "duplicate" (§4.4). */
  seen_op_ids: Record<string, true>;
  /** dedup_keys already folded in — a suggestion applied twice returns "duplicate", never a second row (§4.4). */
  seen_dedup_keys: Record<string, true>;
}

export type OpStatus = "applied" | "duplicate" | "parked" | "stale";

export interface OpResult {
  op_id: string;
  status: OpStatus;
  reason?: string;
  /**
   * For status "applied": prior values of every field this op overwrote —
   * `null` means "field did not exist before". This is what the server persists
   * as the op-log `pre_fields` column (§4.4: every op is invertible) and what
   * deriveInverseOp consumes. Extension to the §4.5 result shape: pre_fields
   * must be computed at merge time by the ONE merge function, or a second
   * state-reader would exist.
   */
  pre_fields?: FieldValues;
}

export interface ApplyResult {
  state: GigState;
  results: OpResult[];
}

export function emptyGigState(gig_id: string): GigState {
  return {
    gig_id,
    gig: null,
    collections: {},
    seen_op_ids: {},
    seen_dedup_keys: {},
  };
}

export function isItemDeleted(item: Item): boolean {
  return item.fields[TOMBSTONE_FIELD] === true;
}

/** Per-field LWW merge of a patch into an item (mutates the working-copy item). */
function mergeFields(
  item: Item,
  patch: FieldValues,
  hlc: ParsedHLC,
  hlcEncoded: HLC,
): { wonAny: boolean; pre: FieldValues } {
  let wonAny = false;
  const pre: FieldValues = {};
  // Deterministic order regardless of patch key insertion order.
  for (const key of Object.keys(patch).sort()) {
    const incoming = patch[key];
    const existingClock = item._clocks[key];
    // Incoming wins iff strictly newer (or the field has never been written).
    // Ties keep the existing value: equal stamps can only come from the same
    // client (clientId is part of the stamp), i.e. a replay. (Server clamping
    // goes through HLCClamper, which never emits two identical stamps.)
    if (existingClock !== undefined && compareHLC(hlc, existingClock) <= 0) continue;
    pre[key] = key in item.fields ? item.fields[key] : null;
    item.fields[key] = incoming;
    item._clocks[key] = hlcEncoded;
    wonAny = true;
  }
  return { wonAny, pre };
}

function newItem(): Item {
  return { fields: {}, _clocks: {} };
}

/**
 * Fold a batch of ops into a gig's state. Ops are processed in array order;
 * results align 1:1 with the input ops.
 *
 * Statuses:
 *  - applied   — at least one field write won LWW (pre_fields returned)
 *  - duplicate — op_id or dedup_key already folded in; state untouched
 *  - stale     — well-formed, but every touched field lost LWW; state untouched
 *  - parked    — cannot be applied (unknown item/gig, gig mismatch, bad HLC…);
 *                state untouched; caller MUST dead-letter + surface it, never drop
 */
export function applyOps(state: GigState, ops: readonly Op[]): ApplyResult {
  // Purity: work on a deep copy; never touch the caller's state.
  const next: GigState = structuredClone(state);
  const results: OpResult[] = [];

  for (const op of ops) {
    results.push(applyOne(next, op));
  }
  return { state: next, results };
}

function applyOne(state: GigState, op: Op): OpResult {
  const { op_id } = op;

  if (op.gig_id !== state.gig_id) {
    return { op_id, status: "parked", reason: `gig_mismatch: op is for ${op.gig_id}, state is ${state.gig_id}` };
  }

  const parsedHlc = tryParseHLC(op.hlc);
  if (parsedHlc === null) {
    return { op_id, status: "parked", reason: "invalid_hlc" };
  }

  if (state.seen_op_ids[op_id] === true) {
    return { op_id, status: "duplicate", reason: "op_id_already_applied" };
  }
  if (op.dedup_key !== undefined && state.seen_dedup_keys[op.dedup_key] === true) {
    return { op_id, status: "duplicate", reason: "dedup_key_already_applied" };
  }

  const outcome = applyKind(state, op, parsedHlc);

  // parked ops are NOT marked seen: once the cause is fixed (e.g. the missing
  // item arrives), the very same op can be replayed and must then apply.
  if (outcome.status !== "parked") {
    state.seen_op_ids[op_id] = true;
    if (op.dedup_key !== undefined) state.seen_dedup_keys[op.dedup_key] = true;
  }
  return outcome;
}

function applyKind(state: GigState, op: Op, hlc: ParsedHLC): OpResult {
  const { op_id } = op;

  switch (op.kind) {
    case "create_gig": {
      if (state.gig === null) {
        state.gig = newItem();
        const { pre } = mergeFields(state.gig, op.fields, hlc, op.hlc);
        return { op_id, status: "applied", pre_fields: pre };
      }
      // Gig already exists (concurrent create, or the create_gig-as-undo of a
      // delete_gig — see deriveInverseOp): plain per-field LWW merge.
      const { wonAny, pre } = mergeFields(state.gig, op.fields, hlc, op.hlc);
      return wonAny
        ? { op_id, status: "applied", pre_fields: pre }
        : { op_id, status: "stale", reason: "all_fields_lost_lww" };
    }

    case "delete_gig": {
      if (state.gig === null) {
        return { op_id, status: "parked", reason: "unknown_gig" };
      }
      const patch: FieldValues = { ...op.fields, [TOMBSTONE_FIELD]: true };
      const { wonAny, pre } = mergeFields(state.gig, patch, hlc, op.hlc);
      return wonAny
        ? { op_id, status: "applied", pre_fields: pre }
        : { op_id, status: "stale", reason: "tombstone_lost_lww" };
    }

    case "create_item": {
      if (state.gig === null) {
        // create-vs-update discipline extends upward: items belong to a created gig.
        return { op_id, status: "parked", reason: "unknown_gig" };
      }
      const byId = (state.collections[op.target.collection] ??= {});
      const existing = byId[op.target.item_id];
      if (existing === undefined) {
        const item = newItem();
        byId[op.target.item_id] = item;
        const { pre } = mergeFields(item, op.fields, hlc, op.hlc);
        return { op_id, status: "applied", pre_fields: pre };
      }
      // Same-id create from another device: converge by per-field LWW merge.
      // Note this can NOT resurrect a tombstoned item — `deleted` is only
      // touched if the create explicitly carries it and wins that field.
      const { wonAny, pre } = mergeFields(existing, op.fields, hlc, op.hlc);
      return wonAny
        ? { op_id, status: "applied", pre_fields: pre }
        : { op_id, status: "stale", reason: "all_fields_lost_lww" };
    }

    case "put_fields":
    case "delete":
    case "restore": {
      if (state.gig === null) {
        return { op_id, status: "parked", reason: "unknown_gig" };
      }
      const item = state.collections[op.target.collection]?.[op.target.item_id];
      if (item === undefined) {
        // §4.3: an update to an unknown item_id parks as a conflict, NEVER a silent upsert.
        return { op_id, status: "parked", reason: "unknown_item" };
      }
      let patch: FieldValues;
      if (op.kind === "delete") {
        patch = { ...op.fields, [TOMBSTONE_FIELD]: true };
      } else if (op.kind === "restore") {
        patch = { ...op.fields, [TOMBSTONE_FIELD]: false };
      } else {
        patch = op.fields;
        if (Object.keys(patch).length === 0) {
          // A put_fields touching nothing can neither win nor lose a field.
          return { op_id, status: "stale", reason: "empty_fields" };
        }
      }
      const { wonAny, pre } = mergeFields(item, patch, hlc, op.hlc);
      return wonAny
        ? { op_id, status: "applied", pre_fields: pre }
        : {
            op_id,
            status: "stale",
            reason: op.kind === "put_fields" ? "all_fields_lost_lww" : "tombstone_lost_lww",
          };
    }
  }
}
